import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { steps, images, faqs } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

function HowItWorksHero() {
  return (
    <section className="bg-brand-green text-brand-cream py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${images.interior1})` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-brand-green/60 to-brand-green" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">How it works</span>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-tight max-w-3xl">
          From missed calls to booked showings — in four calm steps.
        </h1>
        <p className="mt-6 text-lg text-brand-cream/80 max-w-2xl">
          Setup takes less than 10 minutes. After that, LISTING AiGENT handles the phones so you can handle the closings.
        </p>
      </div>
    </section>
  );
}

function StepCard({ step }) {
  return (
    <div className="group grid grid-cols-1 md:grid-cols-12 gap-8 items-center p-8 md:p-10 rounded-2xl bg-white border border-brand-cream-dark hover:shadow-xl transition-all duration-300">
      <div className="md:col-span-2">
        <span className="font-serif text-6xl md:text-7xl text-brand-green/20 group-hover:text-brand-gold transition-colors duration-300">
          {step.num}
        </span>
      </div>
      <div className="md:col-span-10">
        <h3 className="font-serif text-3xl text-brand-green mb-3">{step.title}</h3>
        <p className="text-brand-green/70 text-lg leading-relaxed">{step.desc}</p>
      </div>
    </div>
  );
}

function StepsSection() {
  return (
    <section className="py-24">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="space-y-6">
          {steps.map((s) => (<StepCard key={s.num} step={s} />))}
        </div>
      </div>
    </section>
  );
}

function FAQItem({ faq }) {
  return (
    <AccordionItem value={faq.q} className="bg-white border border-brand-cream-dark rounded-xl px-6">
      <AccordionTrigger className="text-left text-brand-green font-serif text-lg py-5 hover:no-underline">
        {faq.q}
      </AccordionTrigger>
      <AccordionContent className="text-brand-green/70 leading-relaxed pb-5">
        {faq.a}
      </AccordionContent>
    </AccordionItem>
  );
}

function FAQSection() {
  return (
    <section className="py-24 bg-brand-cream-dark/40">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center mb-14">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-green/60">FAQ</span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-green">Common questions</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f) => (<FAQItem key={f.q} faq={f} />))}
        </Accordion>
      </div>
    </section>
  );
}

function HowItWorksCTASection() {
  return (
    <section className="py-24">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
        <h2 className="font-serif text-4xl md:text-5xl text-brand-green">Ready in 10 minutes.</h2>
        <p className="mt-5 text-brand-green/70 text-lg">Activate for $10. Try it for 7 days. Upgrade only if it earns its keep.</p>
        <Link to="/download" className="mt-8 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg bg-brand-green text-brand-cream font-semibold hover:scale-[1.02] transition-transform">
          Start 7-day trial <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

export default function HowItWorks() {
  return (
    <div className="bg-brand-cream">
      <HowItWorksHero />
      <StepsSection />
      <FAQSection />
      <HowItWorksCTASection />
    </div>
  );
}
